import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
// services
import { MasterService } from "../../../services/master.service";
import { MessageService } from "../../../services/message.service";
import { StoreService } from "../../../services/store.service";
import { SecurityService } from "src/app/services/security.service";
import * as languageLibrary from '../../../services/language';
import { ReceivableSummary } from "src/app/models/receivable-summary.model";
import { Receivable } from "src/app/models/receivable.model";
import { Lot } from "src/app/models/lot.model";
import moment from "moment";
import { Subscription } from "rxjs";

@Component({
    selector: "pastdueanalysis-report",
    templateUrl: "./pastdueanalysis-report.component.html",
    styleUrls: ["./pastdueanalysis-report.component.scss"],
    standalone: false
})
export class PastDueAnalysisReportComponent implements OnInit, OnDestroy {

    /** define if is loading */ 
    protected loading: boolean = false

    // save the language
    protected language: string | null = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = languageLibrary.language

    /** */
    protected lots: Lot[] = []

    // The search form group
    protected form: FormGroup | null = null

    /** A list of summaries grouped by lot. */
    protected summary: ReceivableSummary | null = null

    /** A list of receivables for the selected lot and/or corporation. */
    protected receivables: Receivable[] | null = null

    /** Pre-computed receivables groups to quickly respond to interactivity changes.  */
    protected grouped: Object = {}

    // A debounce timer for search events. Used to wait for 
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> | null = null

    /** Listen for changes to the lot filter. */
    private subscriptions: Subscription[] = []

    /** If the report is filtered by lot this will have a positive value. */
    protected get lotId () {
        return parseInt(this.form.get('lotId').value)
    }

    constructor(private master: MasterService, private ms: MessageService, protected store: StoreService, private router: Router, private fb: FormBuilder, protected security: SecurityService) {
        this.form = this.fb.group({
            lotId: new FormControl<number>(0, []),
            group: new FormControl<string>('method', [])
        })
    }

    ngOnInit() {
        this.subscriptions.push(
            this.form.get('lotId').valueChanges.subscribe((id) => {
                this.reset()
                this.fetch()
            })
        )

        this.fetch()

        if (this.store && this.store.userAccount && this.store.userAccount['user'] && 
            this.store.userAccount['user'].lots) {
            this.lots = this.store.userAccount['user'].lots
        }
    }

    ngOnDestroy() {
        this.reset()

        if (this.watcher) {
            clearTimeout(this.watcher)
            this.watcher = null
        }

        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
    }

    /**
     * Reset the pagination parameters and scroll to the top
    */
    private reset() {
        this.summary = null
        this.receivables = []
        this.grouped = {}

        const list = document.querySelector('#listing')
        if (!list) {
            return
        }

        list.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        })
    }

    /**
     * Get the summary and list of receivables needed for the report.
     */
    protected fetch = async () => {
        if (this.security.notPermitted('reports', 'past due analysis', 'view')) {
            return
        }

        if (this.loading) { // already getting the data
            return
        }

        let lotParam = ''
        let id = parseInt(this.form.get('lotId').value)
        if (id && (!isNaN(id) && (id > 0))) {
            lotParam = `?lot=${id}`
        }

        this.loading = true
        await this.master.getAsync(`reports/pastdueanalysis${lotParam}`).then(res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }
            
            this.summary = res.data.summary
            this.receivables = res.data.receivables
            this.constructGroups()
        })
    }

    /**
     * Group the receivables for later display based on user selections.
     */
    protected constructGroups = () => {
        if (!this.receivables || (this.receivables.length <= 0)) {
            return
        }

        for (const receivable of this.receivables) {
            const key: string = `${receivable.corporateId}-${receivable.dealershipId}-${receivable.lotId}`
            if (!this.grouped.hasOwnProperty(key)) {
                this.grouped[key] = {
                    name: receivable.lot,
                    summary: new ReceivableSummary(),
                    count: 0,
                    method: {
                        sie: {
                            display: 'Scheduled Installment Earnings',
                            summary: new ReceivableSummary(),
                            receivables: new Array<Receivable>,
                            count: 0
                        },
                        tde: {
                            display: 'True Daily Earnings',
                            summary: new ReceivableSummary(),
                            receivables: new Array<Receivable>,
                            count: 0
                        }
                    },
                    days: {
                        oneToFifteen: {
                            display: '1-15 Days Past Due',
                            summary: new ReceivableSummary(),
                            receivables: new Array<Receivable>,
                            count: 0
                        },
                        sixteenToThirty: {
                            display: '16-30 Days Past Due',
                            summary: new ReceivableSummary(),
                            receivables: new Array<Receivable>,
                            count: 0
                        },
                        thirtyOneToSixty: {
                            display: '31-60 Days Past Due',
                            summary: new ReceivableSummary(),
                            receivables: new Array<Receivable>,
                            count: 0
                        },
                        sixtyOneToNinety: {
                            display: '61-90 Days Past Due',
                            summary: new ReceivableSummary(),
                            receivables: new Array<Receivable>,
                            count: 0
                        },
                        aboveNinety: {
                            display: '91 and Above Days Past Due',
                            summary: new ReceivableSummary(),
                            receivables: new Array<Receivable>,
                            count: 0
                        }
                    }
                }
            }

            this.grouped[key].count++
            if (!this.grouped[key].summary.asOf || moment(receivable.day).isAfter(this.grouped[key].summary.asOf)) {
                this.grouped[key].summary.asOf = receivable.day
            }

            this.grouped[key].summary.count++
            this.grouped[key].summary.paymentBalance += receivable.paymentBalance
            this.grouped[key].summary.balance += receivable.balance
            this.grouped[key].summary.taxBalance += receivable.taxBalance
            this.grouped[key].summary.payoff += receivable.payoff
            this.grouped[key].summary.unearnedInterest += receivable.unearnedInterest
            this.grouped[key].summary.unpaidEarnedInterest += receivable.unpaidEarnedInterest
            this.grouped[key].summary.lateFees += receivable.lateFees
            this.grouped[key].summary.ridingCash += receivable.ridingCash

            const daysPastDue = moment().diff(receivable.pastDueDate || receivable.due, 'days')

            switch (true) {
                case (daysPastDue <= 0):
                    this.grouped[key].summary.current++
                    this.grouped[key].summary.currentAmt += receivable.amount
                    this.grouped[key].summary.currentBalance += receivable.balance
                    break
                case (daysPastDue <= 15):
                    this.grouped[key]['days']['oneToFifteen'].receivables.push(receivable)
                    this.grouped[key]['days']['oneToFifteen'].count++
                    this.grouped[key]['days']['oneToFifteen'].summary.paymentBalance += receivable.paymentBalance
                    this.grouped[key]['days']['oneToFifteen'].summary.balance += receivable.balance
                    this.grouped[key]['days']['oneToFifteen'].summary.taxBalance += receivable.taxBalance
                    this.grouped[key]['days']['oneToFifteen'].summary.payoff += receivable.payoff
                    this.grouped[key]['days']['oneToFifteen'].summary.unearnedInterest += receivable.unearnedInterest
                    this.grouped[key]['days']['oneToFifteen'].summary.unpaidEarnedInterest += receivable.unpaidEarnedInterest
                    this.grouped[key]['days']['oneToFifteen'].summary.lateFees += receivable.lateFees
                    this.grouped[key]['days']['oneToFifteen'].summary.ridingCash += receivable.ridingCash
                    this.grouped[key].summary.late1To15++
                    this.grouped[key].summary.late1To15Amt += receivable.amount
                    this.grouped[key].summary.late1To15Balance += receivable.balance
                    this.grouped[key].summary.late++
                    this.grouped[key].summary.lateAmt += receivable.amount
                    this.grouped[key].summary.lateBalance += receivable.balance
                    break
                case ((daysPastDue >= 16) && (daysPastDue <= 30)):
                    this.grouped[key]['days']['sixteenToThirty'].receivables.push(receivable)
                    this.grouped[key]['days']['sixteenToThirty'].count++
                    this.grouped[key]['days']['sixteenToThirty'].summary.paymentBalance += receivable.paymentBalance
                    this.grouped[key]['days']['sixteenToThirty'].summary.balance += receivable.balance
                    this.grouped[key]['days']['sixteenToThirty'].summary.taxBalance += receivable.taxBalance
                    this.grouped[key]['days']['sixteenToThirty'].summary.payoff += receivable.payoff
                    this.grouped[key]['days']['sixteenToThirty'].summary.unearnedInterest += receivable.unearnedInterest
                    this.grouped[key]['days']['sixteenToThirty'].summary.unpaidEarnedInterest += receivable.unpaidEarnedInterest
                    this.grouped[key]['days']['sixteenToThirty'].summary.lateFees += receivable.lateFees
                    this.grouped[key]['days']['sixteenToThirty'].summary.ridingCash += receivable.ridingCash
                    this.grouped[key].summary.late16To30++
                    this.grouped[key].summary.late16To30Amt += receivable.amount
                    this.grouped[key].summary.late16To30Balance += receivable.balance
                    this.grouped[key].summary.late++
                    this.grouped[key].summary.lateAmt += receivable.amount
                    this.grouped[key].summary.lateBalance += receivable.balance
                    break
                case ((daysPastDue >= 31) && (daysPastDue <= 60)):
                    this.grouped[key]['days']['thirtyOneToSixty'].receivables.push(receivable)
                    this.grouped[key]['days']['thirtyOneToSixty'].count++
                    this.grouped[key]['days']['thirtyOneToSixty'].summary.paymentBalance += receivable.paymentBalance
                    this.grouped[key]['days']['thirtyOneToSixty'].summary.balance += receivable.balance
                    this.grouped[key]['days']['thirtyOneToSixty'].summary.taxBalance += receivable.taxBalance
                    this.grouped[key]['days']['thirtyOneToSixty'].summary.payoff += receivable.payoff
                    this.grouped[key]['days']['thirtyOneToSixty'].summary.unearnedInterest += receivable.unearnedInterest
                    this.grouped[key]['days']['thirtyOneToSixty'].summary.unpaidEarnedInterest += receivable.unpaidEarnedInterest
                    this.grouped[key]['days']['thirtyOneToSixty'].summary.lateFees += receivable.lateFees
                    this.grouped[key]['days']['thirtyOneToSixty'].summary.ridingCash += receivable.ridingCash
                    this.grouped[key].summary.late16To30++
                    this.grouped[key].summary.late16To30Amt += receivable.amount
                    this.grouped[key].summary.late16To30Balance += receivable.balance
                    this.grouped[key].summary.late++
                    this.grouped[key].summary.lateAmt += receivable.amount
                    this.grouped[key].summary.lateBalance += receivable.balance
                    break
                case ((daysPastDue >= 61) && (daysPastDue <= 90)):
                    this.grouped[key]['days']['sixtyOneToNinety'].receivables.push(receivable)
                    this.grouped[key]['days']['sixtyOneToNinety'].count++
                    this.grouped[key]['days']['sixtyOneToNinety'].summary.paymentBalance += receivable.paymentBalance
                    this.grouped[key]['days']['sixtyOneToNinety'].summary.balance += receivable.balance
                    this.grouped[key]['days']['sixtyOneToNinety'].summary.taxBalance += receivable.taxBalance
                    this.grouped[key]['days']['sixtyOneToNinety'].summary.payoff += receivable.payoff
                    this.grouped[key]['days']['sixtyOneToNinety'].summary.unearnedInterest += receivable.unearnedInterest
                    this.grouped[key]['days']['sixtyOneToNinety'].summary.unpaidEarnedInterest += receivable.unpaidEarnedInterest
                    this.grouped[key]['days']['sixtyOneToNinety'].summary.lateFees += receivable.lateFees
                    this.grouped[key]['days']['sixtyOneToNinety'].summary.ridingCash += receivable.ridingCash
                    this.grouped[key].summary.late61To90++
                    this.grouped[key].summary.late61To90Amt += receivable.amount
                    this.grouped[key].summary.late61To90Balance += receivable.balance
                    this.grouped[key].summary.late++
                    this.grouped[key].summary.lateAmt += receivable.amount
                    this.grouped[key].summary.lateBalance += receivable.balance
                    break
                case (daysPastDue >= 91):
                    this.grouped[key]['days']['aboveNinety'].receivables.push(receivable)
                    this.grouped[key]['days']['aboveNinety'].count++
                    this.grouped[key]['days']['aboveNinety'].summary.paymentBalance += receivable.paymentBalance
                    this.grouped[key]['days']['aboveNinety'].summary.balance += receivable.balance
                    this.grouped[key]['days']['aboveNinety'].summary.taxBalance += receivable.taxBalance
                    this.grouped[key]['days']['aboveNinety'].summary.payoff += receivable.payoff
                    this.grouped[key]['days']['aboveNinety'].summary.unearnedInterest += receivable.unearnedInterest
                    this.grouped[key]['days']['aboveNinety'].summary.unpaidEarnedInterest += receivable.unpaidEarnedInterest
                    this.grouped[key]['days']['aboveNinety'].summary.lateFees += receivable.lateFees
                    this.grouped[key]['days']['aboveNinety'].summary.ridingCash += receivable.ridingCash
                    this.grouped[key].summary.late91AndOver++
                    this.grouped[key].summary.late91AndOverAmt += receivable.amount
                    this.grouped[key].summary.late91AndOverBalance += receivable.balance
                    this.grouped[key].summary.late++
                    this.grouped[key].summary.lateAmt += receivable.amount
                    this.grouped[key].summary.lateBalance += receivable.balance
                    break
            }

            switch (receivable.method.toLocaleLowerCase()) {
                case 'true daily earnings':
                    this.grouped[key]['method']['tde'].receivables.push(receivable)
                    this.grouped[key]['method']['tde'].count++
                    this.grouped[key]['method']['tde'].summary.paymentBalance += receivable.paymentBalance
                    this.grouped[key]['method']['tde'].summary.balance += receivable.balance
                    this.grouped[key]['method']['tde'].summary.taxBalance += receivable.taxBalance
                    this.grouped[key]['method']['tde'].summary.payoff += receivable.payoff
                    this.grouped[key]['method']['tde'].summary.unearnedInterest += receivable.unearnedInterest
                    this.grouped[key]['method']['tde'].summary.unpaidEarnedInterest += receivable.unpaidEarnedInterest
                    this.grouped[key]['method']['tde'].summary.lateFees += receivable.lateFees
                    this.grouped[key]['method']['tde'].summary.ridingCash += receivable.ridingCash
                    break
                case 'scheduled installment earnings':
                    this.grouped[key]['method']['sie'].receivables.push(receivable)
                    this.grouped[key]['method']['sie'].count++
                    this.grouped[key]['method']['sie'].summary.paymentBalance += receivable.paymentBalance
                    this.grouped[key]['method']['sie'].summary.balance += receivable.balance
                    this.grouped[key]['method']['sie'].summary.taxBalance += receivable.taxBalance
                    this.grouped[key]['method']['sie'].summary.payoff += receivable.payoff
                    this.grouped[key]['method']['sie'].summary.unearnedInterest += receivable.unearnedInterest
                    this.grouped[key]['method']['sie'].summary.unpaidEarnedInterest += receivable.unpaidEarnedInterest
                    this.grouped[key]['method']['sie'].summary.lateFees += receivable.lateFees
                    this.grouped[key]['method']['sie'].summary.ridingCash += receivable.ridingCash
                    break
            }
        }
    }

    /**
     * Computed how many says past due the account is based on the given due date.
     * @param {Date} ago The current (or past) due date of the account.
     */
    protected daysAgo = (ago: Date): number => {
        return moment().diff(ago, 'days')
    }
    
    /**
     * listen to output of navbar
    */ 
    protected listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'setPermissions':
                this.lots = this.store.userAccount['user'].lots
                this.reset()
                this.fetch()
                break
            case 'changeLot':
                break
        }
    }
}
